import React, { useState } from "react";
import { Button, Space, Alert, Steps, Typography, Drawer, Descriptions, Tag, Divider } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { getDomainTagStyle } from "./DomainsTable"; // reuse badge style

const { Text } = Typography;

function money(value, currency) {
  if (value === undefined || value === null || value === "") return "-";
  return `${currency || "USD"} ${Number(value).toFixed(2)}`;
}

function JsonBlock({ value }) {
  if (!value) return "-";
  return (
    <pre
      style={{
        margin: 0,
        maxHeight: 220,
        overflow: "auto",
        whiteSpace: "pre-wrap",
        fontSize: 11,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export default function DomainViewDrawer({ domain, open, onClose, onResume }) {
  const [resuming, setResuming] = useState(false);
  if (!domain) return null;
  const progress = domain.provisioningProgress;
  const resume = async () => { if (!onResume || !domain.domainOrderId) return; setResuming(true); try { await onResume(domain.domainOrderId); } finally { setResuming(false); } };

  return (
    <Drawer
      title={
        <span style={{ fontFamily: "Inter", fontSize: 15 }}>
          {`Domain Details: ${domain.domainName}`}
        </span>
      }
      placement="right"
      size={700}
      onClose={onClose}
      open={open}
      styles={{ body: { paddingBottom: 24 } }}
      zIndex={99999}
    >
      {progress && (
        <>
          <Space style={{ marginBottom: 12 }}><Typography.Text strong>Provisioning: {progress.currentStepLabel}</Typography.Text><Button icon={<ReloadOutlined />} loading={resuming} onClick={resume} disabled={!domain.domainOrderId}>Resume order</Button></Space>
          <Steps
            direction="vertical"
            size="small"
            current={progress.currentStep}
            status={progress.failed ? "error" : undefined}
            items={(progress.steps || []).map((step) => ({
              title: step.label,
              description: step.detail,
              status:
                step.status === "FAILED"
                  ? "error"
                  : step.status === "COMPLETE"
                    ? "finish"
                    : step.status === "IN_PROGRESS"
                      ? "process"
                      : "wait",
            }))}
          />
          {progress.errorMessage && <Alert type="error" showIcon message={progress.errorMessage} style={{ marginTop: 12 }} />}
          <Divider />
        </>
      )}

      <Descriptions
        title="Domain"
        column={1}
        bordered
        size="small"
        style={{ fontFamily: "Inter" }}
      >
        <Descriptions.Item label="Domain Name" style={{ fontSize: 13 }}>
          {domain.domainName}
        </Descriptions.Item>
        <Descriptions.Item label="Target Host" style={{ fontSize: 13 }}>
          {domain.targetHost || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Target IP" style={{ fontSize: 13 }}>
          {domain.targetIpAddress || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Primary Domain" style={{ fontSize: 13 }}>
          {domain.primaryDomain ? "Yes" : "No"}
        </Descriptions.Item>
        <Descriptions.Item label="Status" style={{ fontSize: 13 }}>
          <Tag style={getDomainTagStyle("status", domain.status)}>
            {domain.status}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="User Domain Status" style={{ fontSize: 13 }}>
          {domain.userDomainStatus || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Registered At" style={{ fontSize: 13 }}>
          {domain.domainRegisteredAt || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Expires At" style={{ fontSize: 13 }}>
          {domain.domainExpiresAt || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Auto Renew" style={{ fontSize: 13 }}>
          {domain.domainAutoRenew ? "Yes" : "No"}
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      <Descriptions
        title="Website"
        column={1}
        bordered
        size="small"
        style={{ fontFamily: "Inter" }}
      >
        <Descriptions.Item label="Website Name" style={{ fontSize: 13 }}>
          {domain.websiteName || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Website Slug" style={{ fontSize: 13 }}>
          {domain.websiteSlug || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Website ID" style={{ fontSize: 13 }}>
          {domain.websiteId || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Workspace ID" style={{ fontSize: 13 }}>
          {domain.workspaceId || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="User ID" style={{ fontSize: 13 }}>
          {domain.userId || "-"}
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      <Descriptions
        title="DNS"
        column={1}
        bordered
        size="small"
        style={{ fontFamily: "Inter" }}
      >
        <Descriptions.Item label="DNS Status" style={{ fontSize: 13 }}>
          <Tag style={getDomainTagStyle("dnsStatus", domain.dnsStatus)}>
            {domain.dnsStatus}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="DNS Records" style={{ fontSize: 13 }}>
          {domain.dnsRecords?.length
            ? domain.dnsRecords.map((r, i) => (
                <div key={i} style={{ marginBottom: 4 }}>
                  <Text code>{r.type}</Text>{" "}
                  <Text style={{ fontSize: 12 }}>
                    {r.name} → {r.data} (TTL {r.ttl})
                  </Text>
                </div>
              ))
            : "-"}
        </Descriptions.Item>
        {domain.parsedError && (
          <Descriptions.Item label="DNS Error" style={{ fontSize: 13 }}>
            {domain.parsedError.code && (
              <Tag color="red" style={{ marginBottom: 4 }}>
                {domain.parsedError.code}
              </Tag>
            )}
            <div style={{ fontSize: 12, color: "#cf1322" }}>
              {domain.parsedError.message}
            </div>
          </Descriptions.Item>
        )}
      </Descriptions>

      <Divider />

      <Descriptions
        title="Hosting Infrastructure"
        column={1}
        bordered
        size="small"
        style={{ fontFamily: "Inter" }}
      >
        <Descriptions.Item label="Hosting Status" style={{ fontSize: 13 }}>
          <Tag style={getDomainTagStyle("hostingStatus", domain.hostingStatus)}>
            {domain.hostingStatus}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item
          label="SSL Certificate Status"
          style={{ fontSize: 13 }}
        >
          {domain.sslCertificateStatus || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Hosting IP" style={{ fontSize: 13 }}>
          {domain.hostingIpAddress || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Bucket" style={{ fontSize: 13 }}>
          {domain.hostingBucketName || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Backend Bucket" style={{ fontSize: 13 }}>
          {domain.hostingBackendBucket || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="URL Map" style={{ fontSize: 13 }}>
          {domain.hostingUrlMap || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="HTTP Proxy" style={{ fontSize: 13 }}>
          {domain.hostingHttpProxy || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="HTTPS Proxy" style={{ fontSize: 13 }}>
          {domain.hostingHttpsProxy || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="SSL Certificate" style={{ fontSize: 13 }}>
          {domain.hostingSslCertificate || "-"}
        </Descriptions.Item>
        <Descriptions.Item
          label="HTTP Forwarding Rule"
          style={{ fontSize: 13 }}
        >
          {domain.hostingHttpForwardingRule || "-"}
        </Descriptions.Item>
        <Descriptions.Item
          label="HTTPS Forwarding Rule"
          style={{ fontSize: 13 }}
        >
          {domain.hostingHttpsForwardingRule || "-"}
        </Descriptions.Item>
        {domain.hostingErrorMessage && (
          <Descriptions.Item label="Hosting Error" style={{ fontSize: 13 }}>
            <div style={{ fontSize: 12, color: "#cf1322" }}>
              {domain.hostingErrorMessage}
            </div>
          </Descriptions.Item>
        )}
      </Descriptions>

      <Divider />

      <Descriptions
        title="Domain Billing"
        column={1}
        bordered
        size="small"
        style={{ fontFamily: "Inter" }}
      >
        <Descriptions.Item label="Order Status" style={{ fontSize: 13 }}>
          {domain.domainOrderStatus || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Total Charged" style={{ fontSize: 13 }}>
          {money(domain.domainOrderAmount, domain.domainOrderCurrency)}
        </Descriptions.Item>
        <Descriptions.Item label="Provider Base" style={{ fontSize: 13 }}>
          {money(domain.providerBaseAmount, domain.domainOrderCurrency)}
        </Descriptions.Item>
        <Descriptions.Item label="WHOIS Privacy" style={{ fontSize: 13 }}>
          {money(domain.privacyAmount, domain.domainOrderCurrency)}
        </Descriptions.Item>
        <Descriptions.Item label="Markup" style={{ fontSize: 13 }}>
          {money(domain.markupAmount, domain.domainOrderCurrency)}
          {domain.markupPercent ? ` (${domain.markupPercent}%)` : ""}
        </Descriptions.Item>
        <Descriptions.Item label="Payment Charge ID" style={{ fontSize: 13 }}>
          {domain.paymentChargeId || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Payment Processor ID" style={{ fontSize: 13 }}>
          {domain.paymentProcessorId || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="OpenSRS Order ID" style={{ fontSize: 13 }}>
          {domain.providerOrderId || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="OpenSRS Domain ID" style={{ fontSize: 13 }}>
          {domain.providerDomainId || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Transaction Snapshot" style={{ fontSize: 13 }}>
          <JsonBlock value={domain.transactionSnapshot} />
        </Descriptions.Item>
      </Descriptions>
    </Drawer>
  );
}
